import {
  Alert,
  Box,
  Button,
  Chip,
  Icon,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Typography,
} from "@outschool/backpack";
import { CurrentSubscriptionStatus } from "@outschool/gql-backend-generated";
import { faEllipsisV, faPlus } from "@outschool/icons";
import { useLocalStorageState } from "@outschool/local-storage";
import { Trans, useTranslation } from "@outschool/localization";
import { IntegrationCategory, useTrackEvent } from "@outschool/ui-analytics";
import { ExternalLink } from "@outschool/ui-components-shared";
import { LoadingIcon, Modal } from "@outschool/ui-legacy-component-library";
import _ from "lodash";
import React from "react";

import * as Env from "../../../shared/Env";
import {
  useCreateSetupIntentWithPaymentElement,
  useDeletePaymentMethod,
  usePaymentMethods,
  useSetBackUpPaymentMethod,
  useSetDefaultPaymentMethod,
} from "../../hooks/CurrentUser";
import { useSubscriptionCreditBalance } from "../../hooks/useSubscriptionCreditBalance";
import ErrorMessage from "../ErrorMessage";
import {
  StripeProvider,
  useDefaultStripeErrorMessage,
} from "../payments/Stripe";
import StripeNewCard from "../payments/StripeNewCard";
import { PaymentMethodLineItem } from "../payments/StripeSavedCards";

type PaymentMethodFormOptions = {
  title?: string;
  description?: string;
  buttonLabel?: string;
};
type PaymentMethodModalProps = {
  open: boolean;
};
type PaymentMethodModalContentProps = {
  onClose?: () => void;
  onPaymentSaved?: () => Promise<void>;
  onGoBack?: () => void;
  formOptions?: PaymentMethodFormOptions;
  closeAfterSave?: boolean;
  isCoppaConsentCheck?: boolean;
};

/**
 * This is a consumer for <StripeProvider /> thus the nested components
 */
function SavePaymentMethodModalContent({
  onClose = () => {},
  onPaymentSaved = async () => {},
  onGoBack,
  formOptions = {},
  closeAfterSave = true,
  isCoppaConsentCheck = false,
}: PaymentMethodModalContentProps) {
  const { t } = useTranslation(
    "client\\components\\account\\SavePaymentMethod",
  );
  const [isCardComplete, setIsCardComplete] = React.useState(false);
  const [viewMode, setViewMode] = React.useState<
    "paymentMethods" | "saveNewCard" | "saveBackup"
  >("paymentMethods");

  const errorMessage = useDefaultStripeErrorMessage();
  const [
    setAsBackup,
    { loading: setAsBackupLoading, error: setAsBackupError },
  ] = useSetBackUpPaymentMethod();
  const [
    setAsDefault,
    { loading: setAsDefaultLoading, error: setAsDefaultError },
  ] = useSetDefaultPaymentMethod();

  const { title = null, description = null, buttonLabel = null } = formOptions;

  const primaryButtonLabel =
    buttonLabel ?? viewMode === "saveNewCard"
      ? t`Save Card`
      : viewMode === "saveBackup"
      ? t`Save my Backup Card`
      : t`Save`;

  const {
    loading: paymentMethodsLoading,
    error: paymentMethodsError,
    paymentMethods,
    refetch: refetchPaymentMethods,
  } = usePaymentMethods();
  const defaultPaymentMethod =
    paymentMethods.find(paymentMethod => paymentMethod.isCustomerDefault) ??
    paymentMethods[0];

  const [
    createSetupIntentWithPaymentElement,
    { error: setupIntentError, loading: setupIntentLoading },
  ] = useCreateSetupIntentWithPaymentElement(undefined, true);

  const { currentStatus: currentMembershipStatus, loading: membershipLoading } =
    useSubscriptionCreditBalance();
  const trackEvent = useTrackEvent(IntegrationCategory.Functional);

  const hasDefaultPaymentMethod = !!defaultPaymentMethod;
  const hasAnyBackupPaymentMethods = paymentMethods.some(
    paymentMethod => !!paymentMethod.isBackUpForOffSessionCharges,
  );

  const [isBackupAlertDismissed, setIsBackupAlertDismissed] =
    useLocalStorageState("SavedPaymentMethod.isBackupAlertDismissed", false);

  React.useEffect(() => {
    if (!paymentMethodsLoading && !membershipLoading) {
      trackEvent("save_payment_method_modal_view", {
        membershipStatus: currentMembershipStatus,
        hasDefaultPaymentMethod,
      });
    }
  }, [
    trackEvent,
    currentMembershipStatus,
    paymentMethodsLoading,
    membershipLoading,
    hasDefaultPaymentMethod,
  ]);

  const [isSavingCard, setIsSavingCard] = React.useState<boolean>(false);

  const handleSaveCard = React.useCallback(async () => {
    setIsSavingCard(true);
    try {
      // saving card with setup intent causes us to change the default payment method
      // save current default payment method for use later to fix
      const defaultPaymentMethodUid = defaultPaymentMethod?.uid;
      const result = await createSetupIntentWithPaymentElement();
      const paymentMethodId =
        typeof result.payment_method === "string"
          ? result.payment_method
          : result.payment_method?.id;

      if (
        viewMode === "saveBackup" &&
        paymentMethodId &&
        paymentMethodId !== defaultPaymentMethodUid
      ) {
        await setAsDefault(defaultPaymentMethodUid);
        await setAsBackup(paymentMethodId, true);
      }

      // refetch payment methods to ensure cache matches reality, then call onPaymentSaved to tell parent
      await refetchPaymentMethods();
      await onPaymentSaved();

      if (closeAfterSave) {
        onClose();
      } else {
        setViewMode("paymentMethods");
      }
    } catch (error) {
      // ensure we refetch PMs here because we don't know what state things are in now
      await refetchPaymentMethods();
      console.error("Error saving payment method", error);
    } finally {
      setIsSavingCard(false);
    }
  }, [
    createSetupIntentWithPaymentElement,
    onClose,
    onPaymentSaved,
    closeAfterSave,
    refetchPaymentMethods,
    viewMode,
    setAsBackup,
    setAsDefault,
    defaultPaymentMethod?.uid,
  ]);

  const handleGoBack = React.useCallback(() => {
    if (viewMode !== "paymentMethods") {
      setViewMode("paymentMethods");
      setIsCardComplete(false);
      return;
    }

    if (onGoBack) {
      // for callers that want to have a different behavior than "close" (e.g.
      // go back to a different modal), provide `onGoBack`
      onGoBack();
    } else {
      onClose();
    }
  }, [onGoBack, onClose, viewMode]);

  React.useEffect(() => {
    // Clear success message when changing view modes
    if (viewMode !== "paymentMethods") {
      setSuccessMessage(null);
    }
  }, [viewMode]);

  React.useEffect(() => {
    if (
      !paymentMethodsLoading &&
      !paymentMethodsError &&
      !defaultPaymentMethod
    ) {
      setViewMode("saveNewCard");
    }
  }, [paymentMethodsLoading, paymentMethodsError, defaultPaymentMethod]);

  const disableActionButtons =
    setupIntentLoading ||
    setAsDefaultLoading ||
    setAsBackupLoading ||
    isSavingCard;

  const error =
    setupIntentError ||
    setAsBackupError ||
    setAsDefaultError ||
    paymentMethodsError ||
    null;

  const [successMessage, setSuccessMessage] = React.useState<{
    title: string;
    message: string;
  } | null>(null);

  return (
    <Box>
      <Typography variant="h3" sx={{}}>
        {title
          ? title
          : viewMode === "saveNewCard"
          ? t`Add a New Card`
          : viewMode === "saveBackup"
          ? t`Add a Backup Card`
          : t`Payment methods`}
      </Typography>
      {isCoppaConsentCheck && (
        <Box
          sx={{
            marginTop: "1em",
            marginBottom: "1em",
          }}
        >
          {/* eslint-disable react/no-unescaped-entities */}
          <Trans t={t}>
            <span>
              We collect your credit card info to make sure you're a grown-up
              (not a kid) and to comply with a U.S. law called{" "}
              <ExternalLink url="https://www.ftc.gov/tips-advice/business-center/guidance/complying-coppa-frequently-asked-questions-0">
                The Children's Online Privacy Protection Act (COPPA).
              </ExternalLink>
            </span>
          </Trans>
          {/* eslint-enable react/no-unescaped-entities */}
        </Box>
      )}
      <Box
        flex
        sx={{
          flexDirection: "column",
          gap: 32,
          paddingTop: 8,
          paddingBottom: 24,
        }}
      >
        {paymentMethodsError ? (
          <ErrorMessage
            value={errorMessage}
            showStatusPageLink={Env.IS_READ_ONLY_MODE}
          />
        ) : null}
        {viewMode === "paymentMethods" ? (
          <>
            <Typography variant="body1" color="text.secondary">
              <Trans t={t}>
                Your default card covers{" "}
                <b>all active enrollments and memberships</b>. If a payment
                fails, we’ll automatically use your backup card.
              </Trans>
            </Typography>
            {successMessage && (
              <Alert severity="success" title={successMessage.title}>
                {successMessage.message}
              </Alert>
            )}
            {!!defaultPaymentMethod &&
              !hasAnyBackupPaymentMethods &&
              !isBackupAlertDismissed && (
                <Alert
                  onClose={() => setIsBackupAlertDismissed(true)}
                  severity="info"
                  title={t("Add a backup card")}
                  action={
                    <Button
                      variant="text"
                      color="inherit"
                      size="small"
                      onClick={() => setViewMode("saveBackup")}
                      sx={{ marginTop: 5 }}
                    >
                      {t("Add a New Card")}
                    </Button>
                  }
                >
                  {t(
                    "Never miss a class! If your default card fails, we’ll automatically try your backup.",
                  )}
                </Alert>
              )}
            <PaymentMethodsList
              paymentMethods={paymentMethods}
              setViewMode={setViewMode}
              isLoading={paymentMethodsLoading}
              setSuccessMessage={setSuccessMessage}
            />
          </>
        ) : viewMode === "saveNewCard" ? (
          <>
            <Typography variant="body1" color="text.secondary">
              {description ??
                t`This payment method will be set as your default and be used for all Outschool payments, including memberships and class enrollments.`}
            </Typography>
            {!!error && (
              <Alert severity="error" title={t("We couldn’t add your card.")}>
                {t(
                  "Please check your card details and try again. If the issue continues, try another card or contact support.",
                )}
              </Alert>
            )}
            <StripeNewCard
              setIsCardComplete={setIsCardComplete}
              setBillingDetails={() => {}}
            />
          </>
        ) : viewMode === "saveBackup" ? (
          <>
            {!!error && (
              <Alert
                severity="error"
                title={t("We couldn’t add your backup card.")}
              >
                {t(
                  "Please check your card details and try again. If the issue continues, try another card or contact support.",
                )}
              </Alert>
            )}
            <StripeNewCard
              setIsCardComplete={setIsCardComplete}
              setBillingDetails={() => {}}
            />
          </>
        ) : null}
      </Box>
      <Box flex sx={{ justifyContent: "space-between" }}>
        <Button
          variant="link"
          onClick={handleGoBack}
          disabled={disableActionButtons}
        >
          {viewMode === "paymentMethods" ? t("Cancel") : t("Back")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveCard}
          startIcon={
            setupIntentLoading || setAsBackupLoading || isSavingCard ? (
              <LoadingIcon />
            ) : undefined
          }
          disabled={
            disableActionButtons ||
            viewMode === "paymentMethods" ||
            (viewMode === "saveNewCard" && !isCardComplete) ||
            (viewMode === "saveBackup" && !isCardComplete)
          }
        >
          {primaryButtonLabel}
        </Button>
      </Box>
    </Box>
  );
}

/**
 * Provides stripe when modal is loaded
 */
export function SavePaymentMethodModal({
  open,
  onClose,
  ...otherProps
}: PaymentMethodModalProps & PaymentMethodModalContentProps) {
  return (
    <Modal open={open} onClose={onClose}>
      <SavePaymentMethod onClose={onClose} {...otherProps} />
    </Modal>
  );
}

export function SavePaymentMethod(props: PaymentMethodModalContentProps) {
  return (
    <StripeProvider amountInCents={0}>
      <SavePaymentMethodModalContent {...props} />
    </StripeProvider>
  );
}

function PaymentMethodsList({
  paymentMethods,
  setViewMode,
  isLoading = false,
  setSuccessMessage,
}: {
  paymentMethods: ReturnType<typeof usePaymentMethods>["paymentMethods"];
  setViewMode: (
    viewMode: "paymentMethods" | "saveNewCard" | "saveBackup",
  ) => void;
  isLoading?: boolean;
  setSuccessMessage: React.Dispatch<
    React.SetStateAction<{ title: string; message: string } | null>
  >;
}) {
  const [setAsBackup, { loading: setAsBackupLoading }] =
    useSetBackUpPaymentMethod();

  const [setAsDefault, { loading: setAsDefaultLoading }] =
    useSetDefaultPaymentMethod();

  const [deletePaymentMethod, { loading: deletePaymentMethodLoading }] =
    useDeletePaymentMethod();

  const { t } = useTranslation(
    "client\\components\\account\\SavePaymentMethod",
  );

  const hasAnyBackupPaymentMethods = paymentMethods.some(
    paymentMethod => !!paymentMethod.isBackUpForOffSessionCharges,
  );
  const sortedPaymentMethods = _.orderBy(
    paymentMethods,
    ["isCustomerDefault", "isBackUpForOffSessionCharges"],
    ["desc", "desc"],
  );

  if (isLoading) {
    return (
      <Box flex sx={{ flexDirection: "column", gap: 8 }}>
        <Skeleton variant="rounded" width={"100%"} height={50} />
        <Skeleton variant="rounded" width={"100%"} height={50} />
      </Box>
    );
  }

  const paymentMethodToHighlightForBackup = hasAnyBackupPaymentMethods
    ? undefined
    : sortedPaymentMethods.find(
        paymentMethod => !paymentMethod.isCustomerDefault,
      );

  return (
    <Box flex sx={{ flexDirection: "column", gap: 8 }}>
      {sortedPaymentMethods.map(paymentMethod => (
        <PaymentMethodItem
          key={paymentMethod.uid}
          paymentMethod={paymentMethod}
          setAsBackup={setAsBackup}
          setAsDefault={setAsDefault}
          deletePaymentMethod={deletePaymentMethod}
          showSetAsBackupButton={
            paymentMethodToHighlightForBackup?.uid === paymentMethod.uid
          }
          disabled={
            deletePaymentMethodLoading ||
            setAsBackupLoading ||
            setAsDefaultLoading
          }
          setSuccessMessage={setSuccessMessage}
        />
      ))}
      {paymentMethods.length < 10 && (
        <Box
          onClick={() => setViewMode("saveNewCard")}
          flex
          sx={{
            cursor: "pointer",
            borderRadius: 4,
            border: "1px dashed grey",
            borderColor: "grey.200",
            background: "white",
            paddingY: 12,
            paddingX: 16,
            alignItems: "center",
            gap: 0,
            alignSelf: "stretch",
          }}
        >
          <Button startIcon={<Icon icon={faPlus} />} variant="link">
            {t("Add a New Card")}
          </Button>
        </Box>
      )}
    </Box>
  );
}

const MENU_ITEM_SX = {
  color: "primary.main",
  fontWeight: 500,
  fontSize: 16,
};

function PaymentMethodItem({
  paymentMethod,
  setAsBackup,
  setAsDefault,
  deletePaymentMethod,
  showSetAsBackupButton = false,
  disabled = false,
  setSuccessMessage,
}: {
  paymentMethod: ReturnType<typeof usePaymentMethods>["paymentMethods"][0];
  setAsBackup: (
    paymentMethodId: string,
    isBackUpForOffSessionCharges: boolean,
  ) => Promise<void>;
  setAsDefault: (paymentMethodId: string) => Promise<void>;
  deletePaymentMethod: (paymentMethodId: string) => Promise<void>;
  showSetAsBackupButton?: boolean;
  disabled?: boolean;
  setSuccessMessage: React.Dispatch<
    React.SetStateAction<{ title: string; message: string } | null>
  >;
}) {
  const { currentStatus: currentMembershipStatus, loading: membershipLoading } =
    useSubscriptionCreditBalance();
  const { t } = useTranslation(
    "client\\components\\account\\SavePaymentMethod",
  );
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuIsOpen = Boolean(anchorEl);
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };
  return (
    <Box
      key={paymentMethod.uid}
      flex
      sx={{
        paddingY: 12,
        paddingX: 16,

        alignItems: "center",
        gap: 12,
        alignSelf: "stretch",
        borderRadius: 4,
        border: "1px solid grey",
        borderColor: "grey.200",
        background: "white",
      }}
    >
      <PaymentMethodLineItem paymentMethod={paymentMethod} />
      {paymentMethod.isCustomerDefault ? (
        <Chip color="primary" label={t("Default")} size="small" />
      ) : paymentMethod.isBackUpForOffSessionCharges ? (
        <Chip color="neutral" label={t("Backup")} size="small" />
      ) : null}
      {showSetAsBackupButton && (
        <Button
          variant="link"
          onClick={async () => {
            await setAsBackup(paymentMethod.uid, true);
            setSuccessMessage({
              title: t("Your backup card has been added!"),
              message: t(
                "We’ll use it automatically if your primary payment fails. Update or manage cards anytime in Settings.",
              ),
            });
          }}
          sx={{ marginLeft: "auto" }}
        >
          {t("Set as backup")}
        </Button>
      )}
      <IconButton
        onClick={handleOpenMenu}
        icon={faEllipsisV}
        disabled={disabled || menuIsOpen || membershipLoading}
        color={paymentMethod.isCustomerDefault ? "inherit" : "primary"}
        sx={{
          marginLeft: showSetAsBackupButton
            ? undefined
            : "auto" /** couldn't figure out how to do this with flexbox properties */,
          padding: 4,
          cursor: "pointer",
          borderColor: "transparent",
          color: paymentMethod.isCustomerDefault ? "neutral.main" : "primary",
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={menuIsOpen}
        onClose={handleCloseMenu}
        onClick={handleCloseMenu}
      >
        {paymentMethod.isCustomerDefault &&
        currentMembershipStatus === CurrentSubscriptionStatus.Active ? (
          <>
            <MenuItem sx={{ maxWidth: 304, whiteSpace: "normal" }}>
              {t(
                "You can’t delete your default card while subscribed to a membership. To delete this card, set another card as default or cancel the membership.",
              )}
            </MenuItem>
            <MenuItem
              disabled={true}
              sx={{ ...MENU_ITEM_SX, color: "grey.500" }}
            >
              {t("Delete card")}
            </MenuItem>
          </>
        ) : (
          <>
            <MenuItem
              disabled={!!paymentMethod.isCustomerDefault}
              onClick={async () => {
                await setAsDefault(paymentMethod.uid);
                setSuccessMessage({
                  title: t("Your default card has been updated!"),
                  message: t("All future payments will use this card."),
                });
              }}
              sx={MENU_ITEM_SX}
            >
              {t("Set as default")}
            </MenuItem>
            <MenuItem
              onClick={async () => {
                const isSettingAsBackup =
                  !paymentMethod.isBackUpForOffSessionCharges;
                await setAsBackup(paymentMethod.uid, isSettingAsBackup);
                if (isSettingAsBackup) {
                  setSuccessMessage({
                    title: t("Your backup card has been added!"),
                    message: t(
                      "We’ll use it automatically if your primary payment fails. Update or manage cards anytime in Settings.",
                    ),
                  });
                }
              }}
              sx={MENU_ITEM_SX}
            >
              {paymentMethod.isBackUpForOffSessionCharges
                ? t("Stop using as backup")
                : t("Set as backup")}
            </MenuItem>
            <MenuItem
              sx={{ ...MENU_ITEM_SX, color: "error.600" }}
              onClick={() => deletePaymentMethod(paymentMethod.uid)}
            >
              {t("Delete card")}
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
}
